import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FileText, Download, ChevronDown, ChevronUp, CheckCircle, XCircle, Clock, BarChart2, Users, Sparkles, Upload, X, Save, CalendarDays, ClipboardList, ListOrdered, Vote, Printer, UserCheck, Layers } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom'; // PLENARIUM:CENTRAL_ATAS

const TIPO_LABEL = {
  PROJETO_LEI: 'Projeto de Lei', PROJETO_RESOLUCAO: 'Projeto de Resolução', PROJETO_DECRETO: 'Projeto de Decreto', // PLENARIUM:ATA_SEM_SIGLAS -- nomes completos e acentuados
  REQUERIMENTO: 'Requerimento', MOCAO: 'Moção', INDICACAO: 'Indicação', OUTROS: 'Outros',
  ATA_ANTERIOR: 'Ata da Sessão Anterior'
};
const STATUS_LABEL = { APROVADA: 'Aprovada', REPROVADA: 'Reprovada', PENDENTE: 'Pendente', EM_VOTACAO: 'Em Votacao', RETIRADA: 'Retirada' };
const STATUS_COR   = { APROVADA: 'badge-verde', REPROVADA: 'badge-vermelho', PENDENTE: 'badge-amarelo', EM_VOTACAO: 'badge-vermelho', RETIRADA: 'badge-cinza' };

function numeroParaExtenso(n) {
  const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove',
    'dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
  const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
  const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos',
    'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
  if (n === 0) return 'zero';
  if (n === 100) return 'cem';
  if (n < 20) return unidades[n];
  if (n < 100) return dezenas[Math.floor(n/10)] + (n%10 ? ' e ' + unidades[n%10] : '');
  return centenas[Math.floor(n/100)] + (n%100 ? ' e ' + numeroParaExtenso(n%100) : '');
}
const MESES_EXT = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
const DIAS_SEMANA_EXT = ['domingo','segunda-feira','terça-feira','quarta-feira','quinta-feira','sexta-feira','sábado'];
function dataExtenso(data) {
  const d = new Date(data);
  const dia = d.getUTCDate();
  const diaSemana = DIAS_SEMANA_EXT[d.getUTCDay()];
  const mes = MESES_EXT[d.getUTCMonth()];
  const ano = d.getUTCFullYear();
  const diaExt = numeroParaExtenso(dia);
  const anoExt = ano === 2026 ? 'dois mil e vinte e seis' : ano === 2025 ? 'dois mil e vinte e cinco' : String(ano);
  return `${diaExt} dias do mês de ${mes} de ${anoExt}, ${diaSemana}`;
}
// A data da sessao e' gravada com o horario que foi CADASTRADO, em UTC.
// Por isso lemos os componentes em UTC: converter para o fuso do navegador
// tirava 4 horas em Manaus e imprimia 11:00 no lugar das 15:00 cadastradas.
function dataHoraCadastrada(data) {
  if (!data) return '';
  const d = new Date(data);
  const p = (n) => String(n).padStart(2, '0');
  return p(d.getUTCDate()) + '/' + p(d.getUTCMonth() + 1) + '/' + d.getUTCFullYear() + ' às ' + p(d.getUTCHours()) + ':' + p(d.getUTCMinutes());
}
function horaExtenso(data) {
  const d = new Date(data);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const hExt = numeroParaExtenso(h) + (h === 1 ? ' hora' : ' horas');
  const mExt = m === 0 ? '' : ' e ' + numeroParaExtenso(m) + (m === 1 ? ' minuto' : ' minutos');
  return hExt + mExt;
}
function tempoExtenso(seg) {
  const min = Math.floor(seg / 60);
  const s = seg % 60;
  if (min === 0) return numeroParaExtenso(s) + (s === 1 ? ' segundo' : ' segundos');
  if (s === 0) return numeroParaExtenso(min) + (min === 1 ? ' minuto' : ' minutos');
  return numeroParaExtenso(min) + (min === 1 ? ' minuto' : ' minutos') + ' e ' + numeroParaExtenso(s) + (s === 1 ? ' segundo' : ' segundos');
}

// ── Concordância nos documentos ─────────────────────────────────────
// "Presidente" e "Vice-Presidente" não variam com o sexo (decisão da casa).
// Sem sexo cadastrado, mantém o masculino, que era o comportamento anterior.
function tratDoc(v) {
  if (!v) return 'Vereador';
  if (v.cargo === 'PRESIDENTE') return 'Presidente';
  if (v.cargo === 'VICE_PRESIDENTE') return 'Vice-Presidente';
  return v.sexo === 'F' ? 'Vereadora' : 'Vereador';
}
const artigoTrat = (v) => (v && v.sexo === 'F') ? 'a' : 'o';
// Cabeçalho de uma lista de parlamentares, concordando com quem está nela.
function rotuloLista(lista) {
  const arr = lista || [];
  if (arr.length === 0) return 'os(as) seguintes Vereadores(as)';
  const todasF = arr.every(v => v.sexo === 'F');
  const todosM = arr.every(v => v.sexo !== 'F');
  if (todasF) return 'as seguintes Vereadoras';
  if (todosM) return 'os seguintes Vereadores';
  return 'os(as) seguintes Vereadores(as)';
}

// >>> PLENARIUM:APARTES inicio
// Apartes concedidos por um orador, somando todas as falas dele na sessao
// (a lista da ata mostra cada orador uma vez so). Vem marcados pela Mesa
// na tela da sessao. Sem aparte, devolve '' e a frase fica como antes.
//   ", que concedeu aparte ao Vereador X (PP) e à Vereadora Y (PL)"
function textoApartesOrador(usos, orador, presenca) {
  const chave = (u) => (u.vereadorId || u.nome || '') + '|' + (u.partido || '');
  const alvo = chave(orador);
  const vistos = new Set();
  const itens = [];
  for (const u of usos || []) {
    if (chave(u) !== alvo) continue;
    for (const a of u.apartes || []) {
      if (!a || vistos.has(a.vereadorId)) continue;
      vistos.add(a.vereadorId);
      const ref = (presenca || []).find(p => p.id === a.vereadorId) || {};
      const contracao = artigoTrat(ref) === 'a' ? 'à' : 'ao';
      itens.push(`${contracao} ${tratDoc(ref)} ${a.nome}${a.partido ? ` (${a.partido})` : ''}`);
    }
  }
  if (itens.length === 0) return '';
  const lista = itens.length === 1 ? itens[0] : itens.slice(0, -1).join(', ') + ' e ' + itens[itens.length - 1];
  return ', que concedeu aparte ' + lista;
}
// <<< PLENARIUM:APARTES fim

// >>> PLENARIUM:PARECER_ATA inicio
// Parecer de comissão na ata. Vem dos anexos da matéria do tipo
// "Parecer da Comissão"; entra SÓ a observação, com o nome do documento
// como rótulo. Sem parecer (ou com observação vazia) não sai nada e a
// frase da matéria fica exatamente como antes.
// Rótulo: se o nome já começa com "Parecer", fica como foi digitado;
// senão ganha "Parecer da" na frente ("Comissão de Justiça" vira
// "Parecer da Comissão de Justiça").
function rotuloParecerAta(nome) {
  const n = String(nome || '').trim().replace(/[\s:.;,\-–—]+$/, '');
  if (!n) return 'Parecer da Comissão';
  if (/^parecer/i.test(n)) return n;
  return 'Parecer da ' + n;
}
function textoParecerAta(obs) {
  const t = String(obs || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return /[.!?…]$/.test(t) ? t : t + '.';
}
// Uma linha em itálico por parecer, na ordem em que foram anexados.
// O texto é digitado pelo usuário, por isso passa por _escHtml.
function pareceresMateriaHtml(p) {
  const anexos = (p && p.projeto && p.projeto.anexos) || [];
  return anexos
    .map(a => ({ rotulo: rotuloParecerAta(a.nome), texto: textoParecerAta(a.descricao) }))
    .filter(x => x.texto)
    .map(x => '<br/><em>' + _escHtml(x.rotulo) + ': ' + _escHtml(x.texto) + '</em>')
    .join('');
}
// Trecho entre o título da matéria e o resultado da votação.
// Sem parecer devolve o mesmo texto de antes: "[descrição] — a matéria".
// PLENARIUM:AUTORIA_ATA -- autor(es) da materia (do Tramite), junto do tipo, na ata do sistema
const autoriaAta = (p) => (p.projeto && p.projeto.autor ? `, autoria: ${p.projeto.autor}` : '');
// PLENARIUM:ATA_SEM_SIGLAS -- na ata, a sigla do numero vira o nome completo (facilita a leitura):
// "REQ 037/2026" -> "Requerimento nº 037/2026". Numero sem sigla conhecida fica como esta.
const NOME_SIGLA = { PL: 'Projeto de Lei', PLC: 'Projeto de Lei Complementar', PR: 'Projeto de Resolução', PD: 'Projeto de Decreto', PDL: 'Projeto de Decreto Legislativo', REQ: 'Requerimento', 'MOÇ': 'Moção', MOC: 'Moção', IND: 'Indicação', DOC: 'Documento' };
function numeroSemSigla(numero) {
  const s = String(numero || '').trim();
  const m = s.match(/^(PLC|PDL|PL|PR|PD|REQ|MOÇ|MOC|IND|DOC)(?=[\s.\d/º°-]|$)\.?\s*(?:n[º°o]\.?\s*)?(.*)$/i);
  if (!m) return { texto: s, expandiu: false };
  return { texto: `${NOME_SIGLA[m[1].toUpperCase()]}${m[2] ? ` nº ${m[2].trim()}` : ''}`, expandiu: true };
}
// numero sem sigla + o que vai entre parenteses (o tipo so quando o numero ainda nao diz)
const identAta = (p) => { const n = numeroSemSigla(p.numero); const tipo = n.expandiu ? '' : (TIPO_LABEL[p.tipo] || p.tipo || ''); const au = autoriaAta(p).replace(/^, /, ''); const par = [tipo, au].filter(Boolean).join(', '); return { numero: n.texto, par: par ? ` (${par})` : '' }; };

function inicioMateriaAta(p) {
  const pareceres = pareceresMateriaHtml(p);
  if (!pareceres) return 'a matéria'; // PLENARIUM:ATA_ORDEM_EXPEDIENTE -- so o titulo, sem a descricao
  return pareceres + '<br/>A matéria';
}
// <<< PLENARIUM:PARECER_ATA fim

// Lista de quem usou a palavra, cada vereador uma única vez (sem tempo de fala).
// Mantém a ordem da primeira ocorrência.
function listaFalasUnica(usos, presenca) {
  const vistos = new Set();
  const nomes = [];
  for (const u of usos || []) {
    const chave = (u.vereadorId || u.nome || '') + '|' + (u.partido || '');
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    const ref = (presenca || []).find(p => p.id === u.vereadorId) || {};
    nomes.push(`${artigoTrat(ref)} ${tratDoc(ref)} ${u.nome}${u.partido ? ` (${u.partido})` : ''}` + textoApartesOrador(usos, u, presenca)); // PLENARIUM:APARTES
  }
  return nomes.join('; ');
}

// >>> PLENARIUM:ELEICAO_MESA ata
// Eleicao da Mesa Diretora na ata (PDF e Word). So entram eleicoes
// encerradas (CONCLUIDA ou EMPATE). No voto secreto a ata traz so os
// totais; no aberto, tambem em quem cada vereador votou.
async function buscarEleicoesAta(sessaoId) {
  try { const r = await axios.get(`/api/eleicoes/sessao/${sessaoId}`); return Array.isArray(r.data) ? r.data : []; }
  catch { return []; }
}
const _escAta = (t) => String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const _votosAta = (n) => `${n} voto${n === 1 ? '' : 's'}`;
function textosEleicaoMesa(eleicoes) {
  return (eleicoes || []).filter(e => e.status === 'CONCLUIDA' || e.status === 'EMPATE').map(e => {
    const chapas = [...(e.chapas || [])].sort((a, b) => a.numero - b.numero);
    const nomeChapa = (c) => `Chapa ${c.numero} – ${String(c.nome || '').replace(/^chapa\s+/i, '')}`;
    const composicao = (c) => (c.membros || []).map(m => `${m.cargo}: ${m.nome}`).join('; ');
    let t = `ELEIÇÃO DA MESA DIRETORA: Em seguida, procedeu-se à eleição da Mesa Diretora para o biênio ${e.bienio}, por voto ${e.votoSecreto ? 'secreto' : 'aberto (nominal)'}, concorrendo ${chapas.length === 1 ? 'a seguinte chapa' : 'as seguintes chapas'}: `;
    t += chapas.map(c => `${nomeChapa(c)} (${composicao(c)})`).join('; ') + '. ';
    t += `Apurados os votos, obteve-se o seguinte resultado: ` + chapas.map(c => `${nomeChapa(c)}: ${_votosAta(c.votos || 0)}`).join('; ');
    t += `; abstenções: ${e.abstencoes || 0}; total de votantes: ${e.votaram}.`;
    if (!e.votoSecreto && (e.votosAbertos || []).length) {
      const partes = chapas.map(c => {
        const nomes = e.votosAbertos.filter(v => v.chapaId === c.id).map(v => v.nome);
        return nomes.length ? `na ${nomeChapa(c)}: ${nomes.join(', ')}` : '';
      }).filter(Boolean);
      const abst = e.votosAbertos.filter(v => !v.chapaId).map(v => v.nome);
      if (abst.length) partes.push(`abstiveram-se: ${abst.join(', ')}`);
      if (partes.length) t += ` Votaram ${partes.join('; ')}.`;
    }
    const eleita = chapas.find(c => c.id === e.chapaEleitaId);
    const empatadas = chapas.filter(c => (e.empatadas || []).includes(c.id));
    if (e.status === 'EMPATE') {
      t += ` Verificado o empate entre ${empatadas.map(nomeChapa).join(' e ')}, a definição da chapa eleita ficou a cargo da Mesa, conforme o Regimento Interno.`;
    } else if (eleita) {
      if (e.desempatePor) {
        const empt = chapas.filter(c => c.votos === eleita.votos);
        t += ` Verificado o empate entre ${empt.map(nomeChapa).join(' e ')}, foi declarada eleita a ${nomeChapa(eleita)}, conforme ${e.desempateFundamento || 'o Regimento Interno'}.`;
      } else {
        t += ` Foi declarada eleita a ${nomeChapa(eleita)}, com ${_votosAta(eleita.votos || 0)}.`;
      }
      t += ` A Mesa Diretora para o biênio ${e.bienio} fica assim composta: ${composicao(eleita)}.`;
    }
    return t;
  });
}
function htmlEleicaoMesa(eleicoes) {
  return textosEleicaoMesa(eleicoes).map(t => {
    const [rot, ...resto] = t.split(': ');
    return `<p style="margin:0 0 12px;text-align:justify;"><strong>${_escAta(rot)}:</strong> ${_escAta(resto.join(': '))}</p>`;
  }).join('');
}
// <<< PLENARIUM:ELEICAO_MESA ata

// PLENARIUM:ATAS_VARIAS -- texto da(s) ata(s) apreciada(s) na sessao, cada uma com o seu resultado
function textosAtasAnteriores(sessao) {
  const atas = (sessao.pautas || []).filter(p => p.tipo === 'ATA_ANTERIOR');
  return atas.map((pa, i) => {
    const unAta = unanimidadeAta(pa, sessao); // PLENARIUM:ATA_ORDEM_EXPEDIENTE
    const rAta = pa.status === 'APROVADA' ? (unAta === 'SIM' ? 'sendo aprovada por unanimidade dos vereadores presentes' : 'sendo aprovada')
      : pa.status === 'REPROVADA' ? (unAta === 'NAO' ? 'sendo rejeitada por unanimidade dos vereadores presentes' : 'sendo rejeitada')
      : pa.status === 'RETIRADA' ? 'sendo retirada de apreciação'
      : 'ficando sua apreciação pendente';
    const nAta = !pa.votacaoSecreta && pa.votos && pa.votos.length > 0
      ? ' Votação nominal: ' + pa.votos.map(v => (v.vereador && v.vereador.nome ? v.vereador.nome.split(' ')[0] : '') + ' (' + (v.vereador ? v.vereador.partido : '') + '): ' + (v.opcao === 'SIM' ? 'Sim' : v.opcao === 'NAO' ? 'Não' : 'Abstenção') + (v.mesa ? ' (registrado pela Mesa)' : '')).join(', ') + '.'
      : pa.votacaoSecreta ? ' (Votação secreta.)' : '';
    const qual = atas.length > 1 ? `a ${String(pa.titulo || pa.numero || 'ata').trim()}` : 'a Ata da Sessão Anterior';
    const rotulo = i > 0 ? '' : atas.length > 1 ? 'ATAS DAS SESSÕES ANTERIORES:' : 'ATA DA SESSÃO ANTERIOR:';
    return { rotulo, texto: (i > 0 ? 'Na sequência, foi submetida à apreciação do Plenário ' : 'Em seguida, foi submetida à apreciação do Plenário ') + qual + ', ' + rAta + ', conforme votação registrada no sistema.' + nAta };
  });
}

// PLENARIUM:ATA_QUORUM -- verificacao de quorum na ata do sistema (numeros vindos do relatorio da sessao)
function quorumAtaSistema(sessao) {
  const q = sessao.quorum;
  const out = { abertura: '', naoAberta: false, ordemDia: '', encerramento: '' };
  if (!q || !q.comRegistro) return out;
  const ext = (n) => `${n} (${numeroParaExtenso(n)})`;
  const votou = (p) => p.status === 'APROVADA' || p.status === 'REPROVADA';
  if (q.semQuorumAbertura && !(sessao.pautas || []).some(votou)) {
    out.abertura = ` A Presidência solicitou ao(à) Secretário(a) que procedesse à verificação do quórum. Conforme o registro de presença no sistema, compareceram ${ext(q.presentesAbertura)} vereadores, número inferior ao mínimo de ${ext(q.minimoAbertura)} exigido para a abertura dos trabalhos. Não havendo quórum regimental, a sessão não foi aberta.`;
    out.naoAberta = true;
    out.encerramento = 'Não havendo quórum regimental para a abertura da sessão e nada mais havendo a tratar, foram encerrados os trabalhos.';
    return out;
  }
  out.abertura = q.semQuorumAbertura
    ? ` Feita a verificação do quórum, o registro do sistema indicou ${ext(q.presentesAbertura)} vereadores presentes, abaixo do mínimo de ${ext(q.minimoAbertura)} exigido para a abertura; ainda assim, a Presidência deu prosseguimento aos trabalhos.`
    : ` Feita a verificação do quórum, com ${ext(q.presentesAbertura)} vereadores presentes, havendo número legal, foi declarada aberta a sessão.`;
  const materias = (sessao.pautas || []).filter(p => p.tipo !== 'ATA_ANTERIOR');
  if (q.semQuorumVotacao && materias.length && !materias.some(votou)) {
    out.ordemDia = `Passando-se à Ordem do Dia, foi feita a verificação do quórum para deliberação: conforme o registro de presença no sistema${q.duasChamadas ? ', após a 2ª chamada' : ''}, compareceram ${ext(q.presentesVotacao)} vereadores, número inferior ao mínimo de ${ext(q.minimoVotacao)} exigido para a votação das matérias. Não havendo quórum para deliberação, ${materias.length === 1 ? 'a matéria constante da Ordem do Dia ficou adiada' : 'as matérias constantes da Ordem do Dia ficaram adiadas'} para a próxima sessão.`;
  }
  return out;
}

// >>> PLENARIUM:ATA_ORDEM_EXPEDIENTE -- ata do sistema:
//  - "por unanimidade" quando todos os vereadores presentes (sem contar o Presidente,
//    que tem o voto de minerva) votaram do mesmo jeito;
//  - Pequeno Expediente com os itens cadastrados no Expediente da sessao;
//  - uso da palavra por fase quando houve inscricao na tribuna; o que foi so
//    pelo cronometro fica em "Uso da palavra";
//  - o uso da palavra vem depois da votacao da(s) ata(s).
const ROT_ITEM_EXP = { MATERIA: 'Matéria', COMUNICADO: 'Comunicado', CONVITE: 'Convite', JUSTIFICATIVA: 'Justificativa de ausência', AVISO: 'Aviso', OFICIO: 'Ofício', OUTRO: '' };
async function buscarExtrasAta(sessao) {
  const [exp, trib, est] = await Promise.all([
    axios.get(`/api/leituras/sessao/${sessao.id}/expediente`).then(r => r.data).catch(() => []),
    axios.get(`/api/tribuna/sessao/${sessao.id}`).then(r => r.data).catch(() => []),
    axios.get(`/api/estrutura-sessao${sessao.camaraId ? `?camaraId=${sessao.camaraId}` : ''}`).then(r => r.data).catch(() => null)
  ]);
  return { expedienteItens: Array.isArray(exp) ? exp : [], tribunaListas: Array.isArray(trib) ? trib : [], estruturaAta: est };
}
// 'SIM' / 'NAO' quando foi unanime entre os presentes (sem o Presidente); senao null
function unanimidadeAta(p, sessao) {
  const pres = (sessao.presenca || []).filter(v => v.presente);
  const presId = (sessao.presidente && sessao.presidente.id) || (pres.find(v => v.cargo === 'PRESIDENTE') || {}).id;
  const esperados = pres.filter(v => v.id !== presId).map(v => v.id);
  if (!esperados.length) return null;
  const e = p.estatisticas || {};
  if (p.votacaoSecreta) {
    if (!e.nao && !e.abstencao && (e.sim || 0) >= esperados.length) return 'SIM';
    if (!e.sim && !e.abstencao && (e.nao || 0) >= esperados.length) return 'NAO';
    return null;
  }
  const voto = {};
  (p.votos || []).forEach(v => { const id = v.vereadorId || (v.vereador && v.vereador.id); if (id) voto[id] = v.opcao; });
  const op = voto[esperados[0]];
  if (op !== 'SIM' && op !== 'NAO') return null;
  return esperados.every(id => voto[id] === op) ? op : null;
}
function textoUnanime(p, sessao, html) {
  const u = unanimidadeAta(p, sessao);
  if (!u) return '';
  if (p.status === 'APROVADA' && u === 'SIM') return `foi ${html ? '<strong>APROVADA</strong>' : 'APROVADA'} por unanimidade dos vereadores presentes`;
  if (p.status === 'REPROVADA' && u === 'NAO') return `foi ${html ? '<strong>REPROVADA</strong>' : 'REPROVADA'} por unanimidade dos vereadores presentes`;
  return '';
}
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
// Blocos do expediente e do uso da palavra: antes (antes da Ordem do Dia) e depois (Explicacoes Pessoais etc.)
function blocosFalasAta(sessao, usos) {
  const pres = sessao.presenca || [];
  const nomeOrador = (vid, nome, partido) => { const ref = pres.find(x => x.id === vid) || {}; return `${artigoTrat(ref)} ${tratDoc(ref)} ${nome}${partido ? ` (${partido})` : ''}`; };
  const usados = new Set();
  const listas = (sessao.tribunaListas || []).slice().sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm));
  const quandoInsc = (i) => new Date(i.chamadoEm || i.falouEm || i.criadoEm).getTime();
  const fases = [];
  for (const l of listas) {
    const falaram = (l.inscricoes || []).filter(i => i.status === 'FALOU' || i.status === 'FALANDO').sort((a, b) => quandoInsc(a) - quandoInsc(b));
    if (!falaram.length) continue;
    const nomes = falaram.map(i => {
      const ref = new Date(i.falouEm || i.chamadoEm || Date.now()).getTime();
      let melhor = null; let dist = Infinity;
      (usos || []).forEach((u, k) => { if (usados.has(k) || u.vereadorId !== i.vereadorId) return; const d = Math.abs(new Date(u.criadoEm).getTime() - ref); if (d < dist && d <= 30 * 60000) { dist = d; melhor = k; } });
      if (melhor != null) usados.add(melhor);
      const u = melhor != null ? usos[melhor] : null;
      return nomeOrador(i.vereadorId, i.nomeCompleto || i.vereadorNome, i.partido) + (u ? textoApartesOrador([u], u, pres) : '');
    });
    fases.push({ fase: String(l.fase || 'Uso da Tribuna').trim(), nomes });
  }
  const est = sessao.estruturaAta || {};
  const rotExp = est.expediente === 'UNICO' ? 'EXPEDIENTE' : 'PEQUENO EXPEDIENTE';
  const itens = (sessao.expedienteItens || []).filter(x => !x.oculto);
  const antes = []; const depois = [];
  const doExp = fases.find(f => semAcento(f.fase) === semAcento(rotExp));
  if (itens.length || doExp) {
    const partes = [];
    if (itens.length) partes.push(`Foi dada ciência ao Plenário ${itens.length === 1 ? 'do seguinte documento' : 'dos seguintes documentos'}: ${itens.map(x => (ROT_ITEM_EXP[x.tipo] ? ROT_ITEM_EXP[x.tipo] + ' — ' : '') + _escAta(x.titulo)).join('; ')}.`);
    if (doExp) partes.push(`${itens.length ? 'Em seguida, fizeram' : 'Fizeram'} uso da palavra, por inscrição: ${doExp.nomes.join('; ')}.`);
    antes.push({ rotulo: rotExp + ':', texto: partes.join(' ') });
  }
  for (const f of fases) {
    if (f === doExp) continue;
    const b = { rotulo: _escAta(f.fase).toUpperCase() + ':', texto: `Fizeram uso da palavra, por inscrição: ${f.nomes.join('; ')}.` };
    if (/explica|pessoa|final|encerr/i.test(semAcento(f.fase))) depois.push(b); else antes.push(b);
  }
  const avulsos = (usos || []).filter((u, k) => !usados.has(k));
  if (avulsos.length) antes.push({ rotulo: 'USO DA PALAVRA:', texto: `${fases.length ? 'Também fizeram' : 'Durante a sessão, fizeram'} uso da palavra: ${listaFalasUnica(avulsos, pres)}.` });
  return { antes, depois };
}
// <<< PLENARIUM:ATA_ORDEM_EXPEDIENTE

function gerarPDFSessao(sessao) {
  const camara = sessao.camara?.nome || 'Câmara Municipal';
  const municipio = sessao.camara?.municipio || '';
  const estado = sessao.camara?.estado || 'AM';
  const dataStr = dataExtenso(sessao.data);
  const horaStr = horaExtenso(sessao.data);

  const presentes = (sessao.presenca || []).filter(v => v.presente);
  const ausentes = (sessao.presenca || []).filter(v => !v.presente);
  const presidente = sessao.presidente || presentes.find(v => v.cargo === 'PRESIDENTE') || presentes[0];
  const usos = sessao.usosPalavra || [];

  // Texto narrativo de abertura
  const nomePresidente = presidente ? `${presidente.nome} (${presidente.partido})` : 'o Presidente';
  const textoAbertura = `Aos ${dataStr}, às ${horaStr}, reuniu-se a ${camara}, ${municipio ? `Município de ${municipio}, ` : ''}Estado do ${estado}, sob a Presidência ${artigoTrat(presidente) === 'a' ? 'da' : 'do'} ${artigoTrat(presidente) === 'a' ? 'Vereadora' : 'Vereador'} ${nomePresidente}.`;

  // Presença
  const listaPresentesTexto = presentes.length > 0
    ? `Registraram presença ${rotuloLista(presentes)}: ${presentes.map(v => `${v.nome} (${v.partido})`).join(', ')}.`
    : 'Não houve registro de presença.';
  const listaAusentesTexto = ausentes.length > 0
    ? `Não registraram presença: ${ausentes.map(v => `${v.nome} (${v.partido})${v.faltaJustificada ? ' – falta justificada' : ''}`).join(', ')}.`
    : '';

  // Uso da palavra
  // PLENARIUM:ATA_ORDEM_EXPEDIENTE -- expediente + uso da palavra (por inscricao ou pelo cronometro)
  const blocosFalas = blocosFalasAta(sessao, usos);
  const pBloco = (b) => `<p style="margin:0 0 12px;text-align:justify;">${b.rotulo ? `<strong>${b.rotulo}</strong> ` : ''}${b.texto}</p>`;
  const textoUso = blocosFalas.antes.map(pBloco).join('');
  const textoUsoDepois = blocosFalas.depois.map(pBloco).join('');

  // Pautas
  // A Ata da Sessao Anterior tem fluxo proprio: apreciada ANTES da Ordem do
  // Dia, fora da lista de materias e fora da numeracao delas.
  const pautaAtaAnterior = (sessao.pautas || []).find(p => p.tipo === 'ATA_ANTERIOR');
  const materias = (sessao.pautas || []).filter(p => p.tipo !== 'ATA_ANTERIOR');
  let textoAtaAnteriorH = '';
  if (pautaAtaAnterior) {
    const pa = pautaAtaAnterior;
    const rAta = pa.status === 'APROVADA' ? 'sendo aprovada'
      : pa.status === 'REPROVADA' ? 'sendo rejeitada'
      : pa.status === 'RETIRADA' ? 'sendo retirada de apreciação'
      : 'ficando sua apreciação pendente';
    const nAta = !pa.votacaoSecreta && pa.votos && pa.votos.length > 0
      ? ' Votação nominal: ' + pa.votos.map(v => (v.vereador && v.vereador.nome ? v.vereador.nome.split(' ')[0] : '') + ' (' + (v.vereador ? v.vereador.partido : '') + '): ' + (v.opcao === 'SIM' ? 'Sim' : v.opcao === 'NAO' ? 'Não' : 'Abstenção') + (v.mesa ? ' (registrado pela Mesa)' : '')).join(', ') + '.'
      : pa.votacaoSecreta ? ' (Votação secreta.)' : '';
    textoAtaAnteriorH = '<p style="margin:0 0 12px;text-align:justify;"><strong>ATA DA SESSÃO ANTERIOR:</strong> Em seguida, foi submetida à apreciação do Plenário a Ata da Sessão Anterior, ' + rAta + ', conforme votação registrada no sistema.' + nAta + '</p>';
  }
  textoAtaAnteriorH = textosAtasAnteriores(sessao).map(t => '<p style="margin:0 0 12px;text-align:justify;">' + (t.rotulo ? '<strong>' + t.rotulo + '</strong> ' : '') + t.texto + '</p>').join(''); // PLENARIUM:ATAS_VARIAS
  let textoPautas = '';
  if (materias.length > 0) {
    const pautasVotadas = sessao.pautas.filter(p => ['APROVADA','REPROVADA','RETIRADA'].includes(p.status));
    const pautasPendentes = sessao.pautas.filter(p => p.status === 'PENDENTE');

    textoPautas = `<p style="margin:0 0 12px;text-align:justify;"><strong>ORDEM DO DIA:</strong> Foram apreciadas as seguintes matérias:</p>`;

    materias.forEach((p, i) => {
      const sim = p.estatisticas?.sim || 0;
      const nao = p.estatisticas?.nao || 0;
      const abs = p.estatisticas?.abstencao || 0;
      const resultadoTexto = p.status === 'APROVADA'
        ? `foi <strong>APROVADA</strong> por ${sim} voto(s) favorável(is), ${nao} contrário(s) e ${abs} abstenção(ões)`
        : p.status === 'REPROVADA'
        ? `foi <strong>REPROVADA</strong> por ${sim} voto(s) favorável(is), ${nao} contrário(s) e ${abs} abstenção(ões)`
        : p.status === 'RETIRADA'
        ? `foi <strong>RETIRADA</strong> da pauta`
        : `encontra-se <strong>PENDENTE</strong> de votação`;

      const votosNominaisTexto = !p.votacaoSecreta && p.votos?.length > 0
        ? ` Votação nominal: ${p.votos.map(v => `${v.vereador?.nome?.split(' ')[0]} (${v.vereador?.partido}): ${v.opcao === 'SIM' ? 'Sim' : v.opcao === 'NAO' ? 'Não' : 'Abstenção'}${v.mesa ? ' (registrado pela Mesa)' : '' /* PLENARIUM:VOTO_MESA */}`).join(', ')}.`
        : p.votacaoSecreta ? ' (Votação secreta.)' : '';

      textoPautas += `<p style="margin:0 0 10px;text-align:justify;padding-left:20px;">
        <strong>${i+1}. ${identAta(p).numero} — ${p.titulo}</strong>${identAta(p).par}: ${inicioMateriaAta(p)} ${textoUnanime(p, sessao, true) || resultadoTexto}.${votosNominaisTexto}
      </p>`;
    });
  } else {
    textoPautas = pautaAtaAnterior
      ? `<p style="margin:0 0 12px;text-align:justify;"><strong>ORDEM DO DIA:</strong> Não havia outras matérias pautadas para votação nesta sessão.</p>`
      : `<p style="margin:0 0 12px;text-align:justify;"><strong>ORDEM DO DIA:</strong> Não havia matérias pautadas para votação nesta sessão.</p>`;
  }

  // Encerramento
  const textoEncerramento = `Nada mais havendo a tratar, ${artigoTrat(presidente)} ${tratDoc(presidente)} declarou encerrada a sessão.`;
  const textoAssinatura = `
    <p style="margin:40px 0 4px;text-align:justify;">E, para constar, lavrou-se a presente ata que, após lida e aprovada, será assinada pelo Presidente e pelo Secretário da Mesa.</p>
    <div style="display:flex;justify-content:space-between;margin-top:60px;">
      <div style="text-align:center;width:45%;">
        <div style="border-top:1px solid #000;padding-top:6px;font-size:12px;">
          <strong>${presidente ? presidente.nome : '________________________________'}</strong><br/>
          Presidente
        </div>
      </div>
      <div style="text-align:center;width:45%;">
        <div style="border-top:1px solid #000;padding-top:6px;font-size:12px;">
          <strong>&nbsp;</strong><br/>
          Secretário(a) da Mesa
        </div>
      </div>
    </div>`;

  // PLENARIUM:ATA_QUORUM -- verificacao de quorum (sem quorum de abertura: a sessao nao foi aberta)
  const qa = quorumAtaSistema(sessao);
  if (qa.ordemDia) textoPautas = '<p style="margin:0 0 12px;text-align:justify;"><strong>ORDEM DO DIA:</strong> ' + qa.ordemDia + '</p>';
  const logoHtml = sessao.camara?.logoUrl
    ? `<img src="${sessao.camara.logoUrl}" style="width:70px;height:70px;object-fit:contain;margin-bottom:8px;" /><br/>`
    : '🏛️<br/>';

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>Ata — ${sessao.titulo}</title>
  <style>
    body { font-family: 'Times New Roman', Times, serif; padding: 48px; max-width: 820px; margin: 0 auto; color: #111; font-size: 13px; line-height: 1.8; }
    h1 { font-size: 16px; margin: 4px 0; }
    h2 { font-size: 14px; margin: 4px 0; font-weight: normal; }
    @media print { body { padding: 24px; } }
  </style>
  </head><body>
  <div style="text-align:center;border-bottom:2px solid #1a2d5a;padding-bottom:16px;margin-bottom:24px;">
    ${logoHtml}
    <h1>${camara.toUpperCase()}</h1>
    <h2>ATA DA ${sessao.titulo.toUpperCase()}</h2>
    <p style="font-size:12px;color:#555;margin:4px 0;">Data: ${dataHoraCadastrada(sessao.data)}</p>
  </div>

  <p style="margin:0 0 12px;text-align:justify;">${textoAbertura}${qa.abertura}</p>
  <p style="margin:0 0 12px;text-align:justify;"><strong>PRESENÇA:</strong> ${listaPresentesTexto} ${listaAusentesTexto}</p>
  ${qa.naoAberta ? '' : textoAtaAnteriorH}
  ${qa.naoAberta ? '' : textoUso}
  ${qa.naoAberta ? '' : textoPautas}
  ${qa.naoAberta ? '' : textoUsoDepois}
  ${qa.naoAberta ? '' : htmlEleicaoMesa(sessao.eleicoes)}
  <p style="margin:0 0 12px;text-align:justify;"><strong>ENCERRAMENTO:</strong> ${qa.encerramento || textoEncerramento}</p>
  ${textoAssinatura}
  </body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 500);
}

async function gerarPDFSessaoCompleto(sessao) {
  sessao = { ...sessao, eleicoes: await buscarEleicoesAta(sessao.id) }; // PLENARIUM:ELEICAO_MESA
  sessao = { ...sessao, ...(await buscarExtrasAta(sessao)) }; // PLENARIUM:ATA_ORDEM_EXPEDIENTE
  // Buscar usos da palavra antes de gerar
  try {
    const r = await axios.get(`/api/uso-palavra/sessao/${sessao.id}`);
    gerarPDFSessao({ ...sessao, usosPalavra: r.data });
  } catch {
    gerarPDFSessao(sessao);
  }
}

async function gerarWordSessao(sessao) {
  sessao = { ...sessao, eleicoes: await buscarEleicoesAta(sessao.id) }; // PLENARIUM:ELEICAO_MESA
  sessao = { ...sessao, ...(await buscarExtrasAta(sessao)) }; // PLENARIUM:ATA_ORDEM_EXPEDIENTE
  try {
    const r = await axios.get(`/api/uso-palavra/sessao/${sessao.id}`);
    sessao = { ...sessao, usosPalavra: r.data };
  } catch {}

  const camara = sessao.camara?.nome || 'Câmara Municipal';
  const municipio = sessao.camara?.municipio || '';
  const estado = sessao.camara?.estado || 'AM';
  const dataStr = dataExtenso(sessao.data);
  const horaStr = horaExtenso(sessao.data);
  const presentes = (sessao.presenca || []).filter(v => v.presente);
  const ausentes = (sessao.presenca || []).filter(v => !v.presente);
  const presidente = sessao.presidente || presentes.find(v => v.cargo === 'PRESIDENTE') || presentes[0];
  const usos = sessao.usosPalavra || [];
  const nomePresidente = presidente ? `${presidente.nome} (${presidente.partido})` : 'o Presidente';

  const textoAbertura = `Aos ${dataStr}, às ${horaStr}, reuniu-se a ${camara}, ${municipio ? `Município de ${municipio}, ` : ''}Estado do ${estado}, sob a Presidência ${artigoTrat(presidente) === 'a' ? 'da' : 'do'} ${artigoTrat(presidente) === 'a' ? 'Vereadora' : 'Vereador'} ${nomePresidente}.`;
  const listaPresentesTexto = presentes.length > 0
    ? `Registraram presença ${rotuloLista(presentes)}: ${presentes.map(v => `${v.nome} (${v.partido})`).join(', ')}.`
    : 'Não houve registro de presença.';
  const listaAusentesTexto = ausentes.length > 0
    ? `Não registraram presença: ${ausentes.map(v => `${v.nome} (${v.partido})${v.faltaJustificada ? ' – falta justificada' : ''}`).join(', ')}.`
    : '';
  // PLENARIUM:ATA_ORDEM_EXPEDIENTE -- expediente + uso da palavra (por inscricao ou pelo cronometro)
  const blocosFalasW = blocosFalasAta(sessao, usos);
  const pBlocoW = (b) => '<p>' + (b.rotulo ? '<strong>' + b.rotulo + '</strong> ' : '') + b.texto + '</p>';
  const textoUso = blocosFalasW.antes.map(b => (b.rotulo ? b.rotulo + ' ' : '') + b.texto).join('\n\n');

  // A Ata da Sessao Anterior tem fluxo proprio: apreciada ANTES da Ordem do
  // Dia, fora da lista de materias e fora da numeracao delas.
  const pautaAtaAnteriorT = (sessao.pautas || []).find(p => p.tipo === 'ATA_ANTERIOR');
  const materiasT = (sessao.pautas || []).filter(p => p.tipo !== 'ATA_ANTERIOR');
  let textoAtaAnteriorT = '';
  if (pautaAtaAnteriorT) {
    const pa = pautaAtaAnteriorT;
    const rAta = pa.status === 'APROVADA' ? 'sendo aprovada'
      : pa.status === 'REPROVADA' ? 'sendo rejeitada'
      : pa.status === 'RETIRADA' ? 'sendo retirada de apreciação'
      : 'ficando sua apreciação pendente';
    const nAta = !pa.votacaoSecreta && pa.votos && pa.votos.length > 0
      ? ' Votação nominal: ' + pa.votos.map(v => (v.vereador && v.vereador.nome ? v.vereador.nome.split(' ')[0] : '') + ' (' + (v.vereador ? v.vereador.partido : '') + '): ' + (v.opcao === 'SIM' ? 'Sim' : v.opcao === 'NAO' ? 'Não' : 'Abstenção') + (v.mesa ? ' (registrado pela Mesa)' : '')).join(', ') + '.'
      : pa.votacaoSecreta ? ' (Votação secreta.)' : '';
    textoAtaAnteriorT = 'ATA DA SESSÃO ANTERIOR: Em seguida, foi submetida à apreciação do Plenário a Ata da Sessão Anterior, ' + rAta + ', conforme votação registrada no sistema.' + nAta + '\n\n';
  }
  textoAtaAnteriorT = textosAtasAnteriores(sessao).map(t => (t.rotulo ? t.rotulo + ' ' : '') + t.texto + '\n\n').join(''); // PLENARIUM:ATAS_VARIAS
  let textoPautas = '';
  if (materiasT.length > 0) {
    textoPautas = 'ORDEM DO DIA: Foram apreciadas as seguintes matérias:\n\n';
    materiasT.forEach((p, i) => {
      const sim = p.estatisticas?.sim || 0;
      const nao = p.estatisticas?.nao || 0;
      const abs = p.estatisticas?.abstencao || 0;
      const resultadoTexto = p.status === 'APROVADA'
        ? `foi APROVADA por ${sim} voto(s) favorável(is), ${nao} contrário(s) e ${abs} abstenção(ões)`
        : p.status === 'REPROVADA'
        ? `foi REPROVADA por ${sim} voto(s) favorável(is), ${nao} contrário(s) e ${abs} abstenção(ões)`
        : p.status === 'RETIRADA' ? 'foi RETIRADA da pauta' : 'encontra-se PENDENTE de votação';
      const votosNominaisTexto = !p.votacaoSecreta && p.votos?.length > 0
        ? ` Votação nominal: ${p.votos.map(v => `${v.vereador?.nome?.split(' ')[0]} (${v.vereador?.partido}): ${v.opcao === 'SIM' ? 'Sim' : v.opcao === 'NAO' ? 'Não' : 'Abstenção'}${v.mesa ? ' (registrado pela Mesa)' : '' /* PLENARIUM:VOTO_MESA */}`).join(', ')}.`
        : p.votacaoSecreta ? ' (Votação secreta.)' : '';
      textoPautas += `${i+1}. ${identAta(p).numero} — ${p.titulo}${identAta(p).par}: ${inicioMateriaAta(p)} ${textoUnanime(p, sessao, false) || resultadoTexto}.${votosNominaisTexto}\n\n`;
    });
  } else {
    textoPautas = pautaAtaAnteriorT
      ? 'ORDEM DO DIA: Não havia outras matérias pautadas para votação nesta sessão.'
      : 'ORDEM DO DIA: Não havia matérias pautadas para votação nesta sessão.';
  }

  const qa = quorumAtaSistema(sessao); // PLENARIUM:ATA_QUORUM
  if (qa.ordemDia) textoPautas = 'ORDEM DO DIA: ' + qa.ordemDia;
  const dataFormatada = dataHoraCadastrada(sessao.data);
  const assinatura = `

E, para constar, lavrou-se a presente ata que, após lida e aprovada, será assinada pelo Presidente e pelo Secretário da Mesa.


_________________________________
${presidente ? presidente.nome : 'Presidente'}
Presidente


_________________________________
Secretário(a) da Mesa`;

  const conteudo = [
    camara.toUpperCase(),
    `ATA DA ${sessao.titulo.toUpperCase()}`,
    `Data: ${dataFormatada}`,
    '',
    textoAbertura,
    '',
    `PRESENÇA: ${listaPresentesTexto} ${listaAusentesTexto}`,
    '',
    textoAtaAnteriorT, // PLENARIUM:ATA_ORDEM_EXPEDIENTE
    textoUso,
    '',
    textoPautas,
    ...textosEleicaoMesa(sessao.eleicoes),
    `ENCERRAMENTO: Nada mais havendo a tratar, ${artigoTrat(presidente)} ${tratDoc(presidente)} declarou encerrada a sessão.`,
    assinatura
  ].filter(Boolean).join('\n');

  // Gerar HTML compatível com Word (.doc) — sem logo no cabeçalho, a pedido

  const wordHtml = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head>
<meta charset="utf-8">
<title>Ata - ${sessao.titulo}</title>
<style>
  body { font-family: 'Times New Roman', serif; font-size: 12pt; margin: 2cm; color: #000; line-height: 1.8; }
  h1 { font-size: 14pt; text-align: center; margin: 4px 0; }
  h2 { font-size: 12pt; text-align: center; font-weight: normal; margin: 4px 0; }
  p { text-align: justify; margin: 0 0 12pt; }
  div.cabecalho-pagina { display: block; border-bottom: 1px solid #1a2d5a; padding-bottom: 8pt; margin-bottom: 8pt; text-align: center; }
  @media print {
    div.cabecalho-pagina { position: running(header); }
    @page { margin-top: 3cm; @top-center { content: element(header); } }
  }
</style>
<!--[if gte mso 9]>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
  </w:WordDocument>
</xml>
<![endif]-->
</head>
<body>

<!-- CABEÇALHO DO DOCUMENTO -->
<div style="text-align:center;border-bottom:2pt solid #1a2d5a;margin-bottom:12pt;padding-bottom:8pt;">
  <strong style="font-size:14pt;">${camara.toUpperCase()}</strong><br/>
  <span style="font-size:10pt;color:#555;">${municipio ? municipio + ' — ' : ''}Estado do ${estado}</span>
</div>

<div style="text-align:center;border-bottom:1pt solid #1a2d5a;padding-bottom:12pt;margin-bottom:18pt;">
  <h2 style="font-size:13pt;font-weight:bold;margin:4px 0;">ATA DA ${sessao.titulo.toUpperCase()}</h2>
  <p style="font-size:10pt;color:#555;margin:4px 0;">Data: ${dataHoraCadastrada(sessao.data)}</p>
</div>

<p>${textoAbertura}${qa.abertura}</p>
<p><strong>PRESENÇA:</strong> ${listaPresentesTexto} ${listaAusentesTexto}</p>
${!qa.naoAberta && textoAtaAnteriorT ? textoAtaAnteriorT.trim().split(/\n\n/).map(t => '<p>' + t + '</p>').join('') : '' /* PLENARIUM:ATAS_VARIAS -- a ata anterior tambem no Word */}
${!qa.naoAberta ? blocosFalasW.antes.map(pBlocoW).join('') : '' /* PLENARIUM:ATA_ORDEM_EXPEDIENTE -- uso da palavra depois da ata */}
${!qa.naoAberta && textoPautas ? '<p>' + textoPautas.replace(/\n\n/g, '</p><p style=\"padding-left:20pt;\">').replace(/\n/g, ' ') + '</p>' : ''}
${!qa.naoAberta ? blocosFalasW.depois.map(pBlocoW).join('') : ''}
${qa.naoAberta ? '' : htmlEleicaoMesa(sessao.eleicoes)}
<p><strong>ENCERRAMENTO:</strong> ${qa.encerramento || `Nada mais havendo a tratar, ${artigoTrat(presidente)} ${tratDoc(presidente)} declarou encerrada a sessão.`}</p>

<p style="margin-top:30pt;">E, para constar, lavrou-se a presente ata que, após lida e aprovada, será assinada pelo Presidente e pelo Secretário da Mesa.</p>

<table style="width:100%;margin-top:60pt;border:none;">
  <tr>
    <td style="width:45%;text-align:center;border-top:1px solid #000;padding-top:6pt;">
      <strong>${presidente ? presidente.nome : '________________________________'}</strong><br/>
      Presidente
    </td>
    <td style="width:10%;"></td>
    <td style="width:45%;text-align:center;border-top:1px solid #000;padding-top:6pt;">
      <strong>&nbsp;</strong><br/>
      Secretário(a) da Mesa
    </td>
  </tr>
</table>
</body></html>`;

  const blob = new Blob([wordHtml], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Ata_' + sessao.titulo.replace(/[^a-zA-Z0-9]/g, '_') + '.doc';
  a.click();
  URL.revokeObjectURL(url);
}

// ===== Geradores da ATA COM IA (texto revisado -> PDF/Word, layout da tradicional, sem logo) =====
function _escHtml(s) {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
// Converte o texto da ata (livre) em parágrafos HTML; linhas em MAIÚSCULAS viram títulos
function _ataTextoParaHtml(texto) {
  return _escHtml(texto).split(/\n{2,}/).map(bloco => {
    const linha = bloco.trim();
    if (/^[A-ZÀ-Ú0-9ºª ,.\-]{3,50}$/.test(linha) && !linha.includes('\n')) {
      return `<p style="text-align:center;font-weight:bold;margin:14px 0 6px;">${linha}</p>`;
    }
    return `<p style="text-align:justify;margin:0 0 10px;">${bloco.replace(/\n/g, '<br/>')}</p>`;
  }).join('\n');
}
async function _dadosCabecalhoAta(sessao) {
  let s = sessao;
  try { const r = await axios.get(`/api/relatorios/sessao/${sessao.id}`); s = { ...sessao, ...r.data }; } catch {}
  return {
    camara: s.camara?.nome || 'Câmara Municipal',
    municipio: s.camara?.municipio || '',
    estado: s.camara?.estado || '',
    titulo: s.titulo || sessao.titulo || 'Sessão',
    data: s.data || sessao.data
  };
}
async function gerarPDFAtaIA(sessao, textoAta) {
  if (!textoAta || !textoAta.trim()) { toast.error('Não há texto de ata para gerar.'); return; }
  const { camara, municipio, estado, titulo, data } = await _dadosCabecalhoAta(sessao);
  const dataFmt = dataHoraCadastrada(data);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ata — ${_escHtml(titulo)}</title>
  <style>body{font-family:'Times New Roman',Times,serif;padding:48px;max-width:820px;margin:0 auto;color:#111;font-size:13px;line-height:1.8;}@media print{body{padding:24px;}}</style>
  </head><body>
  <div style="text-align:center;border-bottom:2px solid #1a2d5a;padding-bottom:16px;margin-bottom:24px;">
    <h1 style="font-size:16px;margin:4px 0;">${_escHtml(camara.toUpperCase())}</h1>
    <p style="font-size:11px;color:#555;margin:2px 0;">${municipio ? _escHtml(municipio) + ' — ' : ''}${estado ? 'Estado do ' + _escHtml(estado) : ''}</p>
    <h2 style="font-size:14px;font-weight:normal;margin:8px 0 4px;">ATA DA ${_escHtml(titulo.toUpperCase())}</h2>
    <p style="font-size:12px;color:#555;margin:4px 0;">Data: ${dataFmt}</p>
  </div>
  ${_ataTextoParaHtml(textoAta)}
  </body></html>`;
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 500);
}
async function gerarWordAtaIA(sessao, textoAta) {
  if (!textoAta || !textoAta.trim()) { toast.error('Não há texto de ata para gerar.'); return; }
  const { camara, municipio, estado, titulo, data } = await _dadosCabecalhoAta(sessao);
  const dataFmt = dataHoraCadastrada(data);
  const wordHtml = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head><meta charset="utf-8"><title>Ata - ${_escHtml(titulo)}</title>
<style>body{font-family:'Times New Roman',serif;font-size:12pt;margin:2cm;color:#000;line-height:1.8;}</style>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom><w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->
</head><body>
<div style="text-align:center;border-bottom:2pt solid #1a2d5a;margin-bottom:12pt;padding-bottom:8pt;">
  <strong style="font-size:14pt;">${_escHtml(camara.toUpperCase())}</strong><br/>
  <span style="font-size:10pt;color:#555;">${municipio ? _escHtml(municipio) + ' — ' : ''}${estado ? 'Estado do ' + _escHtml(estado) : ''}</span>
</div>
<div style="text-align:center;border-bottom:1pt solid #1a2d5a;padding-bottom:12pt;margin-bottom:18pt;">
  <h2 style="font-size:13pt;font-weight:bold;margin:4px 0;">ATA DA ${_escHtml(titulo.toUpperCase())}</h2>
  <p style="font-size:10pt;color:#555;margin:4px 0;">Data: ${dataFmt}</p>
</div>
${_ataTextoParaHtml(textoAta)}
</body></html>`;
  const blob = new Blob([wordHtml], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Nome amigável: "Ata da Sessão - [título] - [câmara]", preservando acentos e
  // espaços; remove apenas os caracteres proibidos em nome de arquivo (\ / : * ? " < > |).
  const nomeCamara = (camara || '').replace(/^C[âa]mara Municipal de\s+/i, 'Câmara de ');
  const nomeArquivo = `Ata da Sessão - ${titulo || 'Sessão'}${nomeCamara ? ' - ' + nomeCamara : ''}`
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  a.download = nomeArquivo + '.doc';
  a.click();
  URL.revokeObjectURL(url);
}


export { gerarPDFAtaIA, gerarWordAtaIA }; // PLENARIUM:CENTRAL_ATAS -- usados na Central de Atas

function SessaoCard({ sessao }) {
  const navigate = useNavigate(); // PLENARIUM:CENTRAL_ATAS
  const [expandida, setExpandida] = useState(false);
  const [ata, setAta] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('pautas');

  // ----- Ata com IA -----
  const [modalIA, setModalIA] = useState(false);
  const [arquivoIA, setArquivoIA] = useState(null);
  const [statusIA, setStatusIA] = useState(null);      // null | TRANSCREVENDO | MONTANDO | CONCLUIDA | ERRO
  const [ataIAId, setAtaIAId] = useState(null);
  const [textoIA, setTextoIA] = useState('');
  // A transcrição fica em estado PRÓPRIO: antes ela dividia espaço com o
  // texto da ata e sumia quando a ata era gerada.
  const [transcricaoIA, setTranscricaoIA] = useState('');
  const [verTranscricao, setVerTranscricao] = useState(false);
  const [erroIA, setErroIA] = useState('');
  const [enviandoIA, setEnviandoIA] = useState(false);
  const [salvandoIA, setSalvandoIA] = useState(false);

  const abrirModalIA = () => {
    setArquivoIA(null); setStatusIA(null); setAtaIAId(null);
    setTextoIA(''); setErroIA(''); setEnviandoIA(false);
    setModalIA(true);
  };

  const enviarAudioIA = async () => {
    if (!arquivoIA) { toast.error('Selecione um arquivo de áudio ou vídeo.'); return; }
    setEnviandoIA(true); setErroIA('');
    try {
      const fd = new FormData();
      fd.append('audio', arquivoIA);
      const r = await axios.post(`/api/ata-ia/${sessao.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAtaIAId(r.data.id);
      setStatusIA('TRANSCREVENDO');
      toast.success('Áudio enviado! A transcrição começou.');
    } catch (e) {
      const msg = e.response?.data?.error || 'Erro ao enviar o áudio.';
      setErroIA(msg); toast.error(msg);
    } finally { setEnviandoIA(false); }
  };

  // Polling: enquanto estiver processando, consulta o status a cada 5s
  useEffect(() => {
    if (!ataIAId || !modalIA) return;
    if (statusIA === 'CONCLUIDA' || statusIA === 'ERRO') return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`/api/ata-ia/${ataIAId}`);
        setStatusIA(r.data.status);
        if (r.data.status === 'CONCLUIDA') {
          setTextoIA(r.data.textoAta || r.data.transcricao || '');
          setTranscricaoIA(r.data.transcricao || '');
          clearInterval(t);
        } else if (r.data.status === 'ERRO') {
          setErroIA(r.data.erro || 'Falha no processamento.');
          clearInterval(t);
        }
      } catch { /* tenta de novo no próximo ciclo */ }
    }, 5000);
    return () => clearInterval(t);
  }, [ataIAId, statusIA, modalIA]);

  const salvarAtaIA = async () => {
    setSalvandoIA(true);
    try {
      await axios.put(`/api/ata-ia/${ataIAId}`, { textoAta: textoIA });
      toast.success('Ata salva.');
    } catch { toast.error('Erro ao salvar a ata.'); }
    finally { setSalvandoIA(false); }
  };

  const carregarAta = async () => {
    if (ata) { setExpandida(!expandida); return; }
    setCarregando(true);
    try {
      const r = await axios.get(`/api/relatorios/sessao/${sessao.id}`);
      setAta(r.data);
      setExpandida(true);
    } catch { toast.error('Erro ao carregar ata'); }
    finally { setCarregando(false); }
  };

  const aprovadas = sessao.pautas?.filter(p => p.status === 'APROVADA').length || 0;
  const reprovadas = sessao.pautas?.filter(p => p.status === 'REPROVADA').length || 0;
  const pendentes  = sessao.pautas?.filter(p => ['PENDENTE','EM_VOTACAO'].includes(p.status)).length || 0;

  const statusBadge = sessao.status === 'ENCERRADA' ? 'badge-cinza' : sessao.status === 'EM_ANDAMENTO' ? 'badge-vermelho' : 'badge-amarelo';
  const statusLabel = sessao.status === 'ENCERRADA' ? 'Encerrada' : sessao.status === 'EM_ANDAMENTO' ? 'Em andamento' : 'Agendada';
  // PLENARIUM:RELATORIOS_VISUAL
  const corSt = sessao.status === 'ENCERRADA' ? '#475569' : sessao.status === 'EM_ANDAMENTO' ? '#dc2626' : '#2563eb';
  const fundoSt = sessao.status === 'ENCERRADA' ? 'rgba(71,85,105,.12)' : sessao.status === 'EM_ANDAMENTO' ? 'rgba(220,38,38,.12)' : 'rgba(37,99,235,.12)';
  const totalP = aprovadas + reprovadas + pendentes;
  const dh = (() => {
    const m = String(dataHoraCadastrada(sessao.data) || '').match(/(\d{2})\/(\d{2})\/(\d{4})(?:\D+(\d{2}:\d{2}))?/);
    if (!m) return { dia: '--', mes: '', ano: '', hora: '' };
    return { dia: m[1], mes: ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'][Number(m[2]) - 1] || '', ano: m[3], hora: m[4] || '' };
  })();

  return (
    <div className={`card rlv-sessao ${expandida ? 'aberta' : ''}`} style={{ borderLeftColor: corSt }}>{/* PLENARIUM:RELATORIOS_VISUAL */}
      <div className="rlv-linha">
        <div className="rlv-data">
          <span className="rlv-dia">{dh.dia}</span>
          <span className="rlv-mes">{dh.mes}</span>
          <span className="rlv-ano">{dh.ano}</span>
        </div>
        <div className="rlv-corpo">
          <div className="rlv-meta">
            <span className="rlv-status" style={{ color: corSt, background: fundoSt }}>{sessao.status === 'EM_ANDAMENTO' && <span className="rlv-pulso" />}{statusLabel}</span>
            <span><Clock size={12} /> {dh.hora ? `às ${dh.hora}` : ''}</span>
            <span>· {totalP} matéria{totalP === 1 ? '' : 's'}</span>
          </div>
          <h3 className="rlv-tit">{sessao.titulo}</h3>
          {totalP > 0 && (
            <div className="rlv-res">
              <div className="rlv-barra">
                {aprovadas > 0 && <span style={{ width: `${(aprovadas / totalP) * 100}%`, background: 'var(--aprovado)' }} />}
                {reprovadas > 0 && <span style={{ width: `${(reprovadas / totalP) * 100}%`, background: 'var(--reprovado)' }} />}
                {pendentes > 0 && <span style={{ width: `${(pendentes / totalP) * 100}%`, background: '#f59e0b' }} />}
              </div>
              <div className="rlv-cont">
                <span><b style={{ color: 'var(--aprovado)' }}>{aprovadas}</b> aprovada{aprovadas === 1 ? '' : 's'}</span>
                <span><b style={{ color: 'var(--reprovado)' }}>{reprovadas}</b> reprovada{reprovadas === 1 ? '' : 's'}</span>
                <span><b style={{ color: '#d97706' }}>{pendentes}</b> pendente{pendentes === 1 ? '' : 's'}</span>
              </div>
            </div>
          )}
        </div>
        <div className="rlv-acoes">
          {ata && (
            <button className="btn btn-primary btn-sm" onClick={() => gerarPDFSessaoCompleto(ata)}>
              <Download size={13} /> PDF
            </button>
          )}
          {ata && (
            <button className="btn btn-secondary btn-sm" onClick={() => gerarWordSessao(ata)}>
              <FileText size={13} /> Word
            </button>
          )}
          <button className="btn btn-secondary btn-sm rlv-ia" onClick={() => navigate(`/atas?sessao=${sessao.id}`)}>{/* PLENARIUM:CENTRAL_ATAS -- a ata agora fica na Central de Atas */}
            <Sparkles size={13} /> Central de Atas
          </button>
          <button className="btn btn-secondary btn-sm" onClick={carregarAta} disabled={carregando}>
            {carregando ? 'Carregando…' : expandida
              ? <><ChevronUp size={13} /> Fechar</>
              : <><ChevronDown size={13} /> Ver ata</>
            }
          </button>
        </div>
      </div>

      {expandida && ata && (
        <div style={{ borderTop: '1px solid var(--cinza)', padding: '16px 20px 20px' }}>
          {/* Abas */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            <button
              className={`btn btn-sm ${abaAtiva === 'pautas' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setAbaAtiva('pautas')}
            >
              <FileText size={13} /> Pautas
            </button>
            <button
              className={`btn btn-sm ${abaAtiva === 'presenca' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setAbaAtiva('presenca')}
            >
              <Users size={13} /> Presença ({ata.resumoPresenca?.totalPresentes}/{ata.resumoPresenca?.total})
            </button>
          </div>

          {/* Aba Pautas */}
          {abaAtiva === 'pautas' && (
            <>
              {ata.pautas.length === 0 ? (
                <p style={{ color: 'var(--texto-suave)', fontSize: '0.825rem' }}>Nenhuma pauta cadastrada.</p>
              ) : ata.pautas.map(p => (
                <div
                  key={p.id}
                  style={{
                    background: 'var(--fundo)',
                    borderRadius: 8,
                    padding: '12px 14px',
                    marginBottom: 8,
                    borderLeft: `3px solid ${p.status === 'APROVADA' ? 'var(--aprovado)' : p.status === 'REPROVADA' ? 'var(--reprovado)' : 'var(--cinza-medio)'}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 5, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span className={`badge ${STATUS_COR[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                        <span className="badge badge-indigo">{TIPO_LABEL[p.tipo]}</span>
                      </div>
                      <p style={{ fontWeight: 700, fontSize: '0.825rem' }}>{p.numero} — {p.titulo}</p>
                      {p.descricao && <p style={{ fontSize: '0.775rem', color: 'var(--texto-suave)', marginTop: 2 }}>{p.descricao}</p>}
                      {p.votos?.length > 0 && (
                        <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--aprovado)', fontWeight: 600 }}>✅ SIM: {p.estatisticas.sim}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--reprovado)', fontWeight: 600 }}>❌ NÃO: {p.estatisticas.nao}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', fontWeight: 600 }}>⚪ ABST: {p.estatisticas.abstencao}</span>
                        </div>
                      )}
                      {!p.votacaoSecreta && p.votos?.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {p.votos.map(v => (
                            <span key={v.id} style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 999, background: v.opcao === 'SIM' ? '#dcfce7' : v.opcao === 'NAO' ? '#fee2e2' : '#f3f4f6', color: v.opcao === 'SIM' ? '#166534' : v.opcao === 'NAO' ? '#991b1b' : '#374151' }}>
                              {v.vereador?.nome?.split(' ')[0]} ({v.vereador?.partido}): {v.opcao === 'SIM' ? 'Sim' : v.opcao === 'NAO' ? 'Não' : 'Abst.'}
                            </span>
                          ))}
                        </div>
                      )}
                      {p.votacaoSecreta && <p style={{ fontSize: '0.7rem', color: 'var(--texto-suave)', marginTop: 3 }}>🔒 Votação secreta</p>}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Aba Presença */}
          {abaAtiva === 'presenca' && (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <div style={{ background: '#dcfce7', borderRadius: 8, padding: '10px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--aprovado)' }}>{ata.resumoPresenca?.totalPresentes}</div>
                  <div style={{ fontSize: '0.72rem', color: '#166534' }}>Presentes</div>
                </div>
                <div style={{ background: '#fee2e2', borderRadius: 8, padding: '10px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--reprovado)' }}>{ata.resumoPresenca?.totalAusentes}</div>
                  <div style={{ fontSize: '0.72rem', color: '#991b1b' }}>Ausentes</div>
                </div>
                <div style={{ background: '#dbeafe', borderRadius: 8, padding: '10px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1d4ed8' }}>{ata.resumoPresenca?.total}</div>
                  <div style={{ fontSize: '0.72rem', color: '#1e40af' }}>Total</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {ata.presenca?.map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 8, background: v.presente ? '#f0fdf4' : '#fff5f5', border: `1px solid ${v.presente ? '#bbf7d0' : '#fecaca'}` }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: v.presente ? 'var(--aprovado)' : 'var(--reprovado)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>
                      {v.fotoUrl
                        ? <img src={v.fotoUrl} alt={v.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : v.nome.split(' ').map(n => n[0]).slice(0,2).join('')
                      }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 700, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.nome}</p>
                      <p style={{ fontSize: '0.7rem', color: 'var(--texto-suave)' }}>{v.partido} · {v.totalVotos} voto(s)</p>
                    </div>
                    <span title={v.faltaJustificada ? 'Falta justificada' : undefined}>{v.presente ? '✅' : v.faltaJustificada ? '📝' : '❌'}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal Ata com IA */}
      {modalIA && (
        <div className="modal-overlay" onClick={() => setModalIA(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720, width: '92%' }}>
            <div className="modal-header">
              <h2 className="modal-title"><Sparkles size={16} color="#7c3aed" /> Ata com Inteligência Artificial</h2>
              <button className="btn-modal-close" onClick={() => setModalIA(false)}><X size={18} /></button>
            </div>

            <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)', marginBottom: 14 }}>
              Sessão: <strong>{sessao.titulo}</strong>
            </p>

            {/* TELA 1: Upload */}
            {!statusIA && (
              <div>
                <p style={{ fontSize: '0.825rem', marginBottom: 12 }}>
                  Envie o áudio ou vídeo da sessão. O sistema vai transcrever a fala e montar um rascunho da ata automaticamente, combinando o que foi dito com os dados da sessão (pautas, votos, presidente).
                </p>
                <div className="form-group">
                  <label className="form-label">Arquivo de áudio ou vídeo</label>
                  <input
                    type="file"
                    accept="audio/*,video/*,.mp3,.m4a,.wav,.ogg,.mp4,.mkv,.mov,.webm"
                    className="form-input"
                    onChange={e => setArquivoIA(e.target.files[0] || null)}
                  />
                  {arquivoIA && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 4 }}>
                      {arquivoIA.name} — {(arquivoIA.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  )}
                </div>
                <div style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 6, padding: '8px 12px', fontSize: '0.72rem', color: 'var(--texto-suave)', marginBottom: 6 }}>
                  ⚠️ Sessões longas levam alguns minutos. Não é possível gerar a ata enquanto houver uma sessão em andamento.
                </div>
                {erroIA && <p style={{ color: 'var(--reprovado)', fontSize: '0.8rem', marginTop: 6 }}>{erroIA}</p>}
                <div className="modal-actions" style={{ marginTop: 14 }}>
                  <button className="btn btn-secondary" onClick={() => setModalIA(false)}>Cancelar</button>
                  <button className="btn btn-primary" onClick={enviarAudioIA} disabled={enviandoIA || !arquivoIA}>
                    {enviandoIA ? 'Enviando...' : <><Upload size={14} /> Enviar e gerar</>}
                  </button>
                </div>
              </div>
            )}

            {/* TELA 2: Processando */}
            {(statusIA === 'TRANSCREVENDO' || statusIA === 'MONTANDO' || statusIA === 'PENDENTE') && (
              <div style={{ textAlign: 'center', padding: '28px 12px' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 6 }}>
                  {statusIA === 'MONTANDO' ? 'Montando a ata...' : 'Transcrevendo o áudio...'}
                </h3>
                <p style={{ fontSize: '0.825rem', color: 'var(--texto-suave)' }}>
                  {statusIA === 'MONTANDO'
                    ? 'A transcrição terminou. A IA está redigindo a ata a partir do que foi falado e dos dados da sessão.'
                    : 'Isso pode levar de alguns segundos a alguns minutos, dependendo da duração da sessão. Pode deixar esta janela aberta.'}
                </p>
                <p style={{ fontSize: '0.72rem', color: 'var(--texto-suave)', marginTop: 14 }}>
                  Etapa: {statusIA === 'MONTANDO' ? '2 de 2 — Redação' : '1 de 2 — Transcrição'}
                </p>
              </div>
            )}

            {/* TELA 3: Erro */}
            {statusIA === 'ERRO' && (
              <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                <XCircle size={40} color="var(--reprovado)" style={{ margin: '0 auto 14px', display: 'block' }} />
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 6 }}>Não foi possível gerar a ata</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--texto-suave)', marginBottom: 16 }}>{erroIA}</p>
                <div className="modal-actions" style={{ justifyContent: 'center' }}>
                  <button className="btn btn-secondary" onClick={abrirModalIA}>Tentar novamente</button>
                </div>
              </div>
            )}

            {/* TELA 4: Revisão */}
            {statusIA === 'CONCLUIDA' && (
              <div>
                <div style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: 6, padding: '8px 12px', fontSize: '0.75rem', marginBottom: 10 }}>
                  ✅ Rascunho gerado. <strong>Revise com atenção</strong> — a transcrição automática pode conter erros. Ajuste o texto abaixo antes de usar como documento oficial.
                </div>
                <textarea
                  className="form-input"
                  value={textoIA}
                  onChange={e => setTextoIA(e.target.value)}
                  style={{ width: '100%', minHeight: 320, fontFamily: 'inherit', fontSize: '0.82rem', lineHeight: 1.5, resize: 'vertical' }}
                />
                <div className="modal-actions" style={{ marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
                  <button className="btn btn-secondary" onClick={() => gerarPDFAtaIA(sessao, textoIA)}>
                    <Download size={14} /> PDF
                  </button>
                  <button className="btn btn-secondary" onClick={() => gerarWordAtaIA(sessao, textoIA)}>
                    <FileText size={14} /> Word
                  </button>
                  {transcricaoIA && (
                    <button className="btn btn-secondary" onClick={() => setVerTranscricao(v => !v)}>
                      <FileText size={14} /> {verTranscricao ? 'Ocultar transcrição' : 'Ver transcrição'}
                    </button>
                  )}
                  <button className="btn btn-secondary" onClick={() => setModalIA(false)}>Fechar</button>
                  <button className="btn btn-primary" onClick={salvarAtaIA} disabled={salvandoIA}>
                    {salvandoIA ? 'Salvando...' : <><Save size={14} /> Salvar ata</>}
                  </button>
                </div>

                {/* Transcrição do áudio — somente leitura, para conferir a ata */}
                {verTranscricao && transcricaoIA && (
                  <div style={{ marginTop: 14, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0f172a' }}>Transcrição do áudio</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          Registro do que o áudio captou. Não é editável — serve para conferir a ata.
                        </div>
                      </div>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { navigator.clipboard.writeText(transcricaoIA); toast.success('Transcrição copiada.'); }}
                      >
                        Copiar
                      </button>
                    </div>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, maxHeight: 300, overflowY: 'auto', fontSize: '0.8rem', lineHeight: 1.6, color: '#334155', whiteSpace: 'pre-wrap' }}>
                      {transcricaoIA}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// PLENARIUM:RELATORIOS_VISUAL -- visual da pagina de Relatorios
const CSS_RLV = `
.rlv-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; margin-bottom: 14px; }
.rlv-kpi { display: flex; align-items: center; gap: 12px; padding: 14px 16px !important; margin: 0 !important; }
.rlv-kpi-ic { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rlv-kpi b { display: block; font-size: 1.5rem; font-weight: 900; line-height: 1.1; color: var(--texto); font-variant-numeric: tabular-nums; }
.rlv-kpi small { font-size: .75rem; color: var(--texto-suave); font-weight: 600; }
.rlv-abas { gap: 6px; }
.rlv-sessao { padding: 0 !important; margin-bottom: 10px; overflow: hidden; border-left: 4px solid var(--cinza); transition: box-shadow .15s; }
.rlv-sessao:hover, .rlv-sessao.aberta { box-shadow: 0 6px 18px rgba(15,23,42,.08); }
.rlv-linha { display: flex; align-items: center; gap: 16px; padding: 14px 18px; flex-wrap: wrap; }
.rlv-data { width: 58px; flex-shrink: 0; text-align: center; border-radius: 12px; background: var(--cinza-claro, rgba(148,163,184,.08)); border: 1px solid var(--cinza); padding: 6px 0; display: flex; flex-direction: column; line-height: 1.1; }
.rlv-dia { font-size: 1.45rem; font-weight: 900; color: var(--texto); font-variant-numeric: tabular-nums; }
.rlv-mes { font-size: .68rem; font-weight: 800; letter-spacing: 1px; color: var(--acento, #2563eb); }
.rlv-ano { font-size: .62rem; color: var(--texto-suave); }
.rlv-corpo { flex: 1; min-width: 220px; }
.rlv-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: .74rem; color: var(--texto-suave); margin-bottom: 3px; }
.rlv-meta span { display: inline-flex; align-items: center; gap: 4px; }
.rlv-status { border-radius: 999px; padding: 3px 10px; font-weight: 800; font-size: .72rem; }
.rlv-pulso { width: 7px; height: 7px; border-radius: 50%; background: currentColor; animation: rlvPulso 1.2s ease infinite; }
@keyframes rlvPulso { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
.rlv-tit { font-size: .98rem; font-weight: 800; color: var(--texto); margin: 0; overflow-wrap: anywhere; }
.rlv-res { margin-top: 8px; max-width: 520px; }
.rlv-barra { display: flex; height: 6px; border-radius: 999px; overflow: hidden; background: var(--cinza); }
.rlv-barra span { height: 100%; }
.rlv-cont { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 6px; font-size: .76rem; color: var(--texto-suave); }
.rlv-cont b { font-size: .9rem; margin-right: 2px; }
.rlv-acoes { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.rlv-ia { color: #7c3aed !important; border-color: #c4b5fd !important; }
.rlv-ia:hover { background: rgba(124,58,237,.08) !important; }
`;

const TIPOS_RELATORIO = [
  { id: 'SESSOES',  label: 'Sessões e atas',    icone: FileText },
  { id: 'PRESENCA', label: 'Lista de presença', icone: ClipboardList },
  { id: 'VOTACAO',  label: 'Votação nominal',   icone: Vote },
  { id: 'PAUTAS',   label: 'Pautas',            icone: ListOrdered }
];
const OPCAO_LABEL = { SIM: 'Sim', NAO: 'Não', ABSTENCAO: 'Abstenção' };
const OPCAO_COR = { SIM: 'badge-verde', NAO: 'badge-vermelho', ABSTENCAO: 'badge-cinza' };

export default function Relatorios() {
  const [dados, setDados] = useState(null);
  const [vereadores, setVereadores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tipo, setTipo] = useState('SESSOES');
  const [situacao, setSituacao] = useState('TODAS');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [sessaoId, setSessaoId] = useState('');
  const [vereadorId, setVereadorId] = useState('');
  const [materiaId, setMateriaId] = useState('');

  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get('/api/relatorios/geral'),
      axios.get('/api/vereadores').catch(() => ({ data: [] }))
    ])
      .then(([g, v]) => {
        setDados(g.data);
        setVereadores(Array.isArray(v.data) ? v.data : (v.data?.vereadores || []));
      })
      .catch(() => toast.error('Não foi possível carregar os relatórios'))
      .finally(() => setLoading(false));
  }, []);

  const todasSessoes = dados?.sessoes || [];

  // Compara pela data cadastrada (os 10 primeiros caracteres do ISO), sem
  // converter fuso — mesma regra usada nos documentos.
  const noPeriodo = (s) => {
    const d = String(s.data).slice(0, 10);
    if (de && d < de) return false;
    if (ate && d > ate) return false;
    return true;
  };

  const sessoesPeriodo = todasSessoes.filter(noPeriodo);
  const sessaoSelecionada = sessoesPeriodo.find(s => s.id === sessaoId) || null;
  const camaraAtual = sessaoSelecionada?.camara || todasSessoes[0]?.camara || null;

  // Ao trocar para um tipo que exige sessão, escolhe a mais recente.
  useEffect(() => {
    if (tipo === 'SESSOES') return;
    if (!sessaoId || !sessoesPeriodo.some(s => s.id === sessaoId)) {
      if (sessoesPeriodo[0]) setSessaoId(sessoesPeriodo[0].id);
    }
  }, [tipo, de, ate, dados]);

  // A presença vem da rota da sessão (ela cruza vereadores ativos x presenças).
  useEffect(() => {
    if (tipo !== 'PRESENCA' || !sessaoId) return;
    setCarregandoDetalhe(true);
    axios.get('/api/relatorios/sessao/' + sessaoId)
      .then(r => setDetalhe(r.data))
      .catch(() => toast.error('Não foi possível carregar a presença desta sessão'))
      .finally(() => setCarregandoDetalhe(false));
  }, [tipo, sessaoId]);

  const rotuloSessao = (s) => dataHoraCadastrada(s.data).slice(0, 10) + ' — ' + s.titulo;

  const pautasDaSessao = sessaoSelecionada?.pautas || [];
  const pautasFiltradas = pautasDaSessao.filter(p => !materiaId || p.id === materiaId);

  const placar = (p) => ({
    sim: (p.votos || []).filter(v => v.opcao === 'SIM').length,
    nao: (p.votos || []).filter(v => v.opcao === 'NAO').length,
    abst: (p.votos || []).filter(v => v.opcao === 'ABSTENCAO').length
  });

  // ---------- impressão ----------
  function imprimir(titulo, subtitulo, corpo) {
    const nome = camaraAtual?.nome || 'Câmara Municipal';
    const municipio = camaraAtual?.municipio || '';
    const estado = camaraAtual?.estado || '';
    const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + _escHtml(titulo) + '</title>'
      + '<style>'
      + "body{font-family:'Times New Roman',Times,serif;padding:40px;max-width:860px;margin:0 auto;color:#111;font-size:13px;}"
      + 'table{width:100%;border-collapse:collapse;margin:8px 0 20px;}'
      + 'th,td{border:1px solid #cbd5e1;padding:6px 9px;text-align:left;font-size:12px;}'
      + 'th{background:#eef2f7;}'
      + 'h2{font-size:13px;margin:18px 0 4px;}'
      + '@media print{body{padding:14px;}}'
      + '</style></head><body>'
      + '<div style="text-align:center;border-bottom:2px solid #1a2d5a;padding-bottom:10px;margin-bottom:16px;">'
      + ((camaraAtual && camaraAtual.logoUrl)
          ? '<img src="' + camaraAtual.logoUrl + '" alt="Brasão" style="width:58px;height:58px;object-fit:contain;display:block;margin:0 auto 6px;" />'
          : '')
      + '<strong style="font-size:15px;">' + _escHtml(nome.toUpperCase()) + '</strong><br/>'
      + '<span style="font-size:11px;color:#555;">' + (municipio ? _escHtml(municipio) + ' — ' : '') + (estado ? 'Estado do ' + _escHtml(estado) : '') + '</span>'
      + '</div>'
      + '<h1 style="font-size:15px;text-align:center;margin:0 0 2px;">' + _escHtml(titulo) + '</h1>'
      + '<p style="font-size:11px;color:#555;text-align:center;margin:0 0 16px;">' + _escHtml(subtitulo || '') + '</p>'
      + corpo
      + '</body></html>';
    const win = window.open('', '_blank');
    if (!win) { toast.error('O navegador bloqueou a janela de impressão'); return; }
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 400);
  }

  function imprimirPresenca() {
    if (!detalhe) return;
    const lista = (detalhe.presenca || []).filter(p => !vereadorId || p.id === vereadorId);
    const linhas = lista.map(p =>
      '<tr><td>' + _escHtml(p.nome) + '</td><td>' + _escHtml(p.partido || '—') + '</td><td>' + (p.presente ? 'Presente' : (p.faltaJustificada ? 'Falta justificada' : 'Ausente')) /* PLENARIUM:JUSTIFICATIVA */ + '</td></tr>'
    ).join('');
    const r = detalhe.resumoPresenca || {};
    const corpo = '<table><thead><tr><th>Vereador(a)</th><th>Partido</th><th>Situação</th></tr></thead><tbody>'
      + linhas + '</tbody></table>'
      + '<p style="font-size:12px;">Presentes: ' + (r.totalPresentes || 0) + ' · Ausentes: ' + (r.totalAusentes || 0) + ' · Total: ' + (r.total || 0) + '</p>'
      + '<table style="width:100%;border:none;margin-top:56px;"><tr>'
      + '<td style="width:45%;text-align:center;border:none;border-top:1px solid #000;padding-top:6px;">'
      + '<strong>' + _escHtml(detalhe.presidente ? detalhe.presidente.nome : ' ') + '</strong><br/>Presidente</td>'
      + '<td style="width:10%;border:none;"></td>'
      + '<td style="width:45%;text-align:center;border:none;border-top:1px solid #000;padding-top:6px;">'
      + '<strong>&nbsp;</strong><br/>Secretário(a) da Mesa</td>'
      + '</tr></table>';
    imprimir('Lista de Presença', detalhe.titulo + ' — ' + dataHoraCadastrada(detalhe.data), corpo);
  }

  function imprimirVotacao() {
    if (!sessaoSelecionada) return;
    const blocos = pautasFiltradas.map(p => {
      const pl = placar(p);
      const cabecalho = '<h2>' + _escHtml(p.numero) + ' — ' + _escHtml(p.titulo) + '</h2>'
        + '<p style="font-size:11px;color:#555;margin:0 0 6px;">Situação: ' + (STATUS_LABEL[p.status] || p.status)
        + ' · Sim: ' + pl.sim + ' · Não: ' + pl.nao + ' · Abstenções: ' + pl.abst + '</p>';
      if (p.votacaoSecreta) {
        return cabecalho + '<p style="font-size:12px;font-style:italic;">Votação secreta — divulgado apenas o resultado.</p>';
      }
      const votos = (p.votos || []).filter(v => !vereadorId || v.vereadorId === vereadorId);
      if (!votos.length) return cabecalho + '<p style="font-size:12px;">Sem votos registrados.</p>';
      const linhas = votos.map(v =>
        '<tr><td>' + _escHtml(v.vereador?.nome || '') + '</td><td>' + _escHtml(v.vereador?.partido || '—') + '</td><td>' + (OPCAO_LABEL[v.opcao] || v.opcao) + '</td></tr>'
      ).join('');
      return cabecalho + '<table><thead><tr><th>Vereador(a)</th><th>Partido</th><th>Voto</th></tr></thead><tbody>' + linhas + '</tbody></table>';
    }).join('');
    const corpo = blocos || '<p>Nenhuma matéria nesta sessão.</p>';
    imprimir('Votação Nominal', sessaoSelecionada.titulo + ' — ' + dataHoraCadastrada(sessaoSelecionada.data), corpo);
  }

  function imprimirPautas() {
    const linhas = pautasParaLista.map(item =>
      '<tr><td>' + _escHtml(item.sessaoTitulo) + '</td><td>' + _escHtml(item.p.numero) + '</td><td>'
      + _escHtml(TIPO_LABEL[item.p.tipo] || item.p.tipo) + '</td><td>' + _escHtml(item.p.titulo) + '</td><td>'
      + (STATUS_LABEL[item.p.status] || item.p.status) + '</td></tr>'
    ).join('');
    const corpo = '<table><thead><tr><th>Sessão</th><th>Nº</th><th>Tipo</th><th>Matéria</th><th>Situação</th></tr></thead><tbody>'
      + (linhas || '<tr><td colspan="5">Nenhuma matéria no período.</td></tr>') + '</tbody></table>';
    const sub = sessaoSelecionada ? sessaoSelecionada.titulo : 'Todas as sessões do período';
    imprimir('Relatório de Pautas', sub, corpo);
  }

  // Lista de pautas usada na aba Pautas (sessão escolhida ou período inteiro)
  const pautasParaLista = (sessaoSelecionada ? [sessaoSelecionada] : sessoesPeriodo)
    .flatMap(s => (s.pautas || []).map(p => ({ p, sessaoTitulo: s.titulo, sessaoData: s.data })));

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const totalSessoes = dados?.totalSessoes || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Relatórios</h1>
          <p className="page-subtitle">Gere documentos de sessões, presença, votação e pautas</p>
        </div>
      </div>

      <style>{CSS_RLV}</style>
      {/* PLENARIUM:RELATORIOS_VISUAL -- indicadores */}
      <div className="rlv-kpis">
        <div className="card rlv-kpi"><span className="rlv-kpi-ic" style={{ background: 'rgba(37,99,235,.12)', color: '#2563eb' }}><BarChart2 size={20} /></span><div><b>{totalSessoes}</b><small>Sessões</small></div></div>
        <div className="card rlv-kpi"><span className="rlv-kpi-ic" style={{ background: 'rgba(22,163,74,.13)', color: '#15803d' }}><CheckCircle size={20} /></span><div><b>{dados?.pautasAprovadas || 0}</b><small>Matérias aprovadas</small></div></div>
        <div className="card rlv-kpi"><span className="rlv-kpi-ic" style={{ background: 'rgba(220,38,38,.12)', color: '#dc2626' }}><XCircle size={20} /></span><div><b>{dados?.pautasReprovadas || 0}</b><small>Matérias reprovadas</small></div></div>
        <div className="card rlv-kpi"><span className="rlv-kpi-ic" style={{ background: 'rgba(217,119,6,.13)', color: '#d97706' }}><Clock size={20} /></span><div><b>{dados?.pautasPendentes || 0}</b><small>Em tramitação</small></div></div>
      </div>

      {/* ─── seletor de tipo ─── */}
      <div className="toolbar rlv-abas" style={{ marginBottom: 12 }}>
        {TIPOS_RELATORIO.map(t => {
          const Icone = t.icone;
          const ativo = tipo === t.id;
          return (
            <button
              key={t.id}
              className={'btn btn-sm ' + (ativo ? 'btn-primary' : 'btn-secondary')}
              onClick={() => { setTipo(t.id); setVereadorId(''); setMateriaId(''); }}
            >
              <Icone size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ─── filtros ─── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label"><CalendarDays size={13} /> De</label>
            <input type="date" className="form-input" value={de} onChange={e => setDe(e.target.value)} style={{ minWidth: 150 }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Até</label>
            <input type="date" className="form-input" value={ate} onChange={e => setAte(e.target.value)} style={{ minWidth: 150 }} />
          </div>

          {tipo === 'SESSOES' && (
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Situação</label>
              <select className="form-input form-select" value={situacao} onChange={e => setSituacao(e.target.value)}>
                <option value="TODAS">Todas</option>
                <option value="ENCERRADA">Encerradas</option>
                <option value="EM_ANDAMENTO">Em andamento</option>
                <option value="AGENDADA">Agendadas</option>
              </select>
            </div>
          )}

          {tipo !== 'SESSOES' && (
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 240 }}>
              <label className="form-label"><Layers size={13} /> Sessão</label>
              <select className="form-input form-select" value={sessaoId} onChange={e => { setSessaoId(e.target.value); setMateriaId(''); }}>
                {tipo === 'PAUTAS' && <option value="">Todas as sessões do período</option>}
                {sessoesPeriodo.map(s => <option key={s.id} value={s.id}>{rotuloSessao(s)}</option>)}
              </select>
            </div>
          )}

          {(tipo === 'PRESENCA' || tipo === 'VOTACAO') && (
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label className="form-label"><UserCheck size={13} /> Parlamentar</label>
              <select className="form-input form-select" value={vereadorId} onChange={e => setVereadorId(e.target.value)}>
                <option value="">Todos</option>
                {vereadores.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
              </select>
            </div>
          )}

          {tipo === 'VOTACAO' && (
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label className="form-label"><FileText size={13} /> Matéria</label>
              <select className="form-input form-select" value={materiaId} onChange={e => setMateriaId(e.target.value)}>
                <option value="">Todas</option>
                {pautasDaSessao.map(p => <option key={p.id} value={p.id}>{p.numero} — {p.titulo}</option>)}
              </select>
            </div>
          )}

          {(de || ate || vereadorId || materiaId) && (
            <button className="btn btn-sm btn-ghost" onClick={() => { setDe(''); setAte(''); setVereadorId(''); setMateriaId(''); }}>
              <X size={14} /> Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* ─── SESSÕES E ATAS ─── */}
      {tipo === 'SESSOES' && (
        sessoesPeriodo.filter(s => situacao === 'TODAS' || s.status === situacao).length === 0
          ? <div className="card sem-dados">Nenhuma sessão no período selecionado.</div>
          : sessoesPeriodo
              .filter(s => situacao === 'TODAS' || s.status === situacao)
              .map(s => <SessaoCard key={s.id} sessao={s} />)
      )}

      {/* ─── LISTA DE PRESENÇA ─── */}
      {tipo === 'PRESENCA' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><ClipboardList size={16} /> Lista de Presença</h2>
            <button className="btn btn-sm btn-secondary" onClick={imprimirPresenca} disabled={!detalhe}>
              <Printer size={14} /> Imprimir
            </button>
          </div>
          {carregandoDetalhe ? <div className="sem-dados">Carregando presença…</div>
            : !detalhe ? <div className="sem-dados">Escolha uma sessão para ver a presença.</div>
            : (
              <>
                <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: 10 }}>
                  {detalhe.titulo} · {dataHoraCadastrada(detalhe.data)} · Presentes: {detalhe.resumoPresenca?.totalPresentes || 0} de {detalhe.resumoPresenca?.total || 0}
                </p>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Vereador(a)</th><th>Partido</th><th>Situação</th></tr></thead>
                    <tbody>
                      {(detalhe.presenca || []).filter(p => !vereadorId || p.id === vereadorId).map(p => (
                        <tr key={p.id}>
                          <td>{p.nome}</td>
                          <td>{p.partido || '—'}</td>
                          <td><span className={'badge ' + (p.presente ? 'badge-verde' : 'badge-vermelho')}>{p.presente ? 'Presente' : (p.faltaJustificada ? 'Falta justificada' : 'Ausente')}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </div>
      )}

      {/* ─── VOTAÇÃO NOMINAL ─── */}
      {tipo === 'VOTACAO' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><Vote size={16} /> Votação Nominal</h2>
            <button className="btn btn-sm btn-secondary" onClick={imprimirVotacao} disabled={!sessaoSelecionada}>
              <Printer size={14} /> Imprimir
            </button>
          </div>
          {!sessaoSelecionada ? <div className="sem-dados">Escolha uma sessão.</div>
            : pautasFiltradas.length === 0 ? <div className="sem-dados">Nenhuma matéria nesta sessão.</div>
            : pautasFiltradas.map(p => {
                const pl = placar(p);
                return (
                  <div key={p.id} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <strong>{p.numero} — {p.titulo}</strong>
                      <span className={'badge ' + (STATUS_COR[p.status] || 'badge-cinza')}>{STATUS_LABEL[p.status] || p.status}</span>
                      <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                        Sim {pl.sim} · Não {pl.nao} · Abstenções {pl.abst}
                      </span>
                    </div>
                    {p.votacaoSecreta ? (
                      <p className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                        Votação secreta — divulgado apenas o resultado.
                      </p>
                    ) : (
                      <div className="table-wrap">
                        <table>
                          <thead><tr><th>Vereador(a)</th><th>Partido</th><th>Voto</th></tr></thead>
                          <tbody>
                            {(p.votos || []).filter(v => !vereadorId || v.vereadorId === vereadorId).map(v => (
                              <tr key={v.id}>
                                <td>{v.vereador?.nome}</td>
                                <td>{v.vereador?.partido || '—'}</td>
                                <td><span className={'badge ' + (OPCAO_COR[v.opcao] || 'badge-cinza')}>{OPCAO_LABEL[v.opcao] || v.opcao}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
        </div>
      )}

      {/* ─── PAUTAS ─── */}
      {tipo === 'PAUTAS' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title"><ListOrdered size={16} /> Pautas</h2>
            <button className="btn btn-sm btn-secondary" onClick={imprimirPautas} disabled={pautasParaLista.length === 0}>
              <Printer size={14} /> Imprimir
            </button>
          </div>
          {pautasParaLista.length === 0 ? <div className="sem-dados">Nenhuma matéria no período selecionado.</div> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Sessão</th><th>Nº</th><th>Tipo</th><th>Matéria</th><th>Situação</th><th>Placar</th></tr></thead>
                <tbody>
                  {pautasParaLista.map(({ p, sessaoTitulo }) => {
                    const pl = placar(p);
                    return (
                      <tr key={p.id}>
                        <td>{sessaoTitulo}</td>
                        <td>{p.numero}</td>
                        <td>{TIPO_LABEL[p.tipo] || p.tipo}</td>
                        <td>{p.titulo}</td>
                        <td><span className={'badge ' + (STATUS_COR[p.status] || 'badge-cinza')}>{STATUS_LABEL[p.status] || p.status}</span></td>
                        <td className="font-mono">{pl.sim}/{pl.nao}/{pl.abst}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
